import React, { useEffect, useRef } from 'react'
import { styled } from '@mui/material/styles'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useTranslation, Trans } from 'react-i18next'
import { Link, useLoaderData } from 'react-router'
import useAuth from '../../hooks/useAuth'
import InfoBlock from '../components/InfoBlock'

import { FactsTable, FactsTableRow } from '../components/FactsTable'

import {
    Button,
    Grid,
    LinearProgress,
    Divider as MuiDivider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'

import { spacing } from '@mui/system'

import { IssueTypeText } from '../../components/entities/IssueType'
import { formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import { useAppLocale } from '../../i18n/localeUtils'
import { SimpleCardBlock } from '../components/SimpleCardBlock'
import IssueComments from './IssueComments'
import Investors from './IssueInvestors'
import { IssuePublicStatusWithExtraInfo } from './IssuePublicStatus'
import { IpoOutcomeOverview } from './OutcomeOverview/IpoOutcomeOverview'
import { IssueOutcomeOverview } from './OutcomeOverview/IssueOutcomeOverview'
import IssueSellers from './IssueSellers'
import Terms from './IssueTerms/IssueTerms'
import TradingStats from './trading-stats/TradingStats'
import UnderwritersAndGuarantors from './IssueUnderwritersAndGuarantors'
import { EnrichedIssueEntity, IssueEntity } from 'types/IssueEntity'
import { WatchListButton } from '../components/WatchListButton'
import { EntityNoteButton } from '../components/EntityNoteButton'
import { hasSourceOfType } from '../../components/utils/calculateIssueStatus'
import { IssueSummary } from './IssueSummary'
import { IssueInfo } from './IssueInfo'

// import IssueCosts from './IssueCosts' // removed for now. Problably relevant for Affärsvärlden, eventually.

const Divider = styled(MuiDivider)(spacing)

const getShowOutcome = (issue: IssueEntity) => {
    if (issue.type === 'IPO') {
        return issue.unitSubTotal !== null || issue.secondarySub !== null
    }
    return (
        issue.unitSubTotal !== null &&
        (issue.unitSubTotalWithRights !== null || issue.unitPctSubWithRights !== null) &&
        (issue.unitSubWithoutRights !== null || issue.unitPctSubWithoutRights !== null) &&
        (issue.unitSubGuarantor !== null || issue.unitPctSubGuarantor !== null || issue.noGuarantors === 1)
    )
}

function DetailsView({ issue }: { issue: EnrichedIssueEntity }) {
    const { t } = useTranslation()
    const theme = useTheme()
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const showOutcome = getShowOutcome(issue)
    const issueSellers = issue.investors.filter((investor) => investor.isSeller)
    const isCancelled = hasSourceOfType(issue, 'Cancellation')

    return (
        <Grid container spacing={6}>
            {isCancelled && (
                <Grid size={12}>
                    <SimpleCardBlock>{t('pages.issues.details.cancelledNotice')}</SimpleCardBlock>
                </Grid>
            )}
            {['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type) &&
            !isCancelled &&
            issue.unitSubTotal === null ? (
                <Grid size={12}>
                    <SimpleCardBlock>
                        <Trans
                            i18nKey='pages.issues.details.announcedNotCompleted'
                            components={{ strong: <strong /> }}
                        />
                    </SimpleCardBlock>
                </Grid>
            ) : null}
            <Grid size={{ xs: 12, md: 6 }}>
                <IssueSummary issue={issue} isLoading={false} isSlim={isMobileScreen} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <IssueInfo issue={issue} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Terms issue={issue} />
            </Grid>
            {!['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type) && showOutcome && (
                // only show if the issue is completed
                <Grid size={{ xs: 12, md: 6 }}>
                    {issue.type === 'IPO' ? (
                        <IpoOutcomeOverview issue={issue} />
                    ) : (
                        <IssueOutcomeOverview issue={issue} />
                    )}
                </Grid>
            )}
            {!['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type) && !showOutcome && (
                // show if the issue is not completed
                <Grid size={{ xs: 12, md: 6 }}>
                    <ImportantDatesBlock issue={issue} />
                </Grid>
            )}
            {['Rights issue', 'IPO'].includes(issue.type) && (
                <Grid size={{ xs: 12, md: 12 }}>
                    <UnderwritersAndGuarantors issue={issue} />
                </Grid>
            )}
            {['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type) && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <Investors issue={issue} />
                </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
                <TradingStats issue={issue} />
            </Grid>
            {issueSellers.length > 0 ? (
                <Grid size={{ xs: 12, md: 6 }}>
                    <IssueSellers issueSellers={issueSellers} />
                </Grid>
            ) : null}
            {!['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type) && showOutcome && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <ImportantDatesBlock issue={issue} />
                </Grid>
            )}
            {(issue.sources.length || (issue.type === 'Non-cash issue' && issue.noncashConsideration)) && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <IssueComments issue={issue} />
                </Grid>
            )}
            {/* removed for now. Problably relevant for Affärsvärlden, eventually.
            <Grid size={{xs: 12, md: 6}}>
                <IssueCosts entity={entity} />
            </Grid>
            */}
        </Grid>
    )
}

function ImportantDatesBlock({ issue }: { issue: IssueEntity }) {
    const { t } = useTranslation()
    return (
        <InfoBlock title={t('pages.issues.details.importantDates')}>
            <ImportantDates issue={issue} />
        </InfoBlock>
    )
}

function ImportantDates({ issue }: { issue: IssueEntity }) {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const i18nPrefix = 'entities.issue.fields.'
    const datesDescription = [
        'recordDate',
        'incRightsDate',
        'exRightsDate',
        'rightsStartDate',
        'rightsEndDate',
        'subStartDate',
        'subEndDate',
        'generalMeetingDate',
        'ipoTradingDate',
    ] as const

    const filteredDates = datesDescription
        .map((dateField) => {
            return {
                name: dateField,
                label: t(`${i18nPrefix}${dateField}`),
                value: issue[dateField],
            }
        })
        .filter((row) => row.value)
        .sort((a, b) => {
            if (!a && !b) return 0
            if (!a) return -1
            if (!b) return 1
            return a.value > b.value ? 1 : -1
        })

    if (filteredDates.length === 0) {
        return <Typography>{t('pages.issues.details.noDatesYet')}</Typography>
    }

    const rows = filteredDates.map((row, i) => (
        <FactsTableRow key={i} label={row.label} value={formatDateYYYYMMDD(row.value, { locale })} valueAlign='left'>
            {formatDateYYYYMMDD(row.value, { locale }) || t('pages.issues.details.notAvailable')}
        </FactsTableRow>
    ))

    return <FactsTable rows={rows} />
}

function getDescriptiveName(issue: IssueEntity, language: string) {
    if (!issue) return ''

    if (issue.type === 'IPO' && issue.ipoTradingDate) {
        return `${issue.shareName} (${formatDateYYYYMMDD(issue.ipoTradingDate, { locale: language })})`
    }

    const date = issue.subStartDate
        ? `${formatDateYYYYMMDD(issue.subStartDate, { locale: language })} - ${formatDateYYYYMMDD(issue.subEndDate, { locale: language })}`
        : formatDateYYYYMMDD(
              issue.sources.find((source) => source.sourceType === 'Publication')?.publishedDate || null,
              { locale: language },
          )

    return `${issue.shareName} (${date})`
}

function IssueDetails() {
    const { user } = useAuth()
    const { t } = useTranslation()
    const { language } = useAppLocale()
    const issue = useLoaderData()
    const isInitialLanguageRender = useRef(true)

    useEffect(() => {
        if (isInitialLanguageRender.current) {
            isInitialLanguageRender.current = false
            return
        }

        window.location.reload()
    }, [language])

    const isCancelled = issue && hasSourceOfType(issue, 'Cancellation')

    return (
        <React.Fragment>
            <Helmet title={t('site.issues.detailsTitle', { name: getDescriptiveName(issue, language) })} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                    fontStyle: isCancelled ? 'italic' : 'normal',
                }}
            >
                {isCancelled ? `${t('pages.issues.details.cancelledPrefix')}` : ''}{' '}
                <IssueTypeText issueType={issue?.type} /> {getDescriptiveName(issue, language)}
                <WatchListButton entityId={issue.id} isInWatchList={!!issue.isInWatchList} type='Issue' />
                <EntityNoteButton entityId={issue.id} type='issue' />
            </Typography>
            <Stack
                direction='row'
                spacing={2}
                sx={{
                    mt: 2,
                    alignItems: 'center',
                }}
            >
                <Typography
                    variant='body2'
                    gutterBottom
                    sx={{
                        display: 'block',
                    }}
                >
                    <IssuePublicStatusWithExtraInfo issue={issue} />
                </Typography>
                {user?.groups?.includes('superadmin') && (
                    <>
                        <Button variant='text' to={`/admin/issues/${issue?.id}`} LinkComponent={Link}>
                            {t('site.admin.editLink')}
                        </Button>

                        <Button
                            variant='text'
                            to={`/admin/pr-company?companyId=${issue.companyId}`}
                            LinkComponent={Link}
                        >
                            {t('site.admin.viewAdminPr')}
                        </Button>
                    </>
                )}
            </Stack>
            <Divider my={6} />
            <Grid container spacing={6}>
                <Grid size={12}>
                    {!issue && <LinearProgress />}
                    {issue && <DetailsView issue={issue} />}
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default IssueDetails
